import DiffMatchPatch from 'diff-match-patch'
import { Marked, type Tokens } from 'marked'

import { kCombinedCitationRegex } from '@/util/agent-citations'

type DiffOperation = 'equal' | 'insert' | 'delete'

type DiffSegment = {
  operation: DiffOperation
  text: string
}

type TokenDiff = {
  operation: DiffOperation
  tokens: string[]
}

type MarkdownBlock = {
  raw: string
  table?: Tokens.Table
}

/** A stretch of a diff: items unchanged on both sides, or items removed and added together. */
type DiffRun<T> = { kind: 'same'; items: T[] } | { kind: 'changed'; removed: T[]; added: T[] }

const kListPrefixRegex = /^(\s*(?:[*-]|\d+\.)\s+)/
// A backslash that ends a line is a line break, not part of the word before it.
const kMarkdownWordTokenRegex = /\*\*|__|~~|`+|[*_]|\\(?=\r?\n)|(?:[^\s*_`~\\]|\\(?!\r?\n))+|~|\s+/g
const markdownParser = new Marked({ gfm: true })

export function buildMatterArtifactEditDiffMarkdown(selectedMarkdown: string, replacementMarkdown: string): string {
  const selectedBlocks = splitTableBlocks(selectedMarkdown)
  const replacementBlocks = splitTableBlocks(replacementMarkdown)
  if (![...selectedBlocks, ...replacementBlocks].some((block) => block.table)) return buildWordDiff(selectedMarkdown, replacementMarkdown)

  return groupDiffRuns(selectedBlocks, replacementBlocks, (block) => block.raw)
    .map((run) => (run.kind === 'same' ? run.items.map((block) => block.raw).join('') : renderChangedBlocks(run.removed, run.added)))
    .join('')
}

/** Diff two lists by key and group the result into runs; unchanged runs hold the replacement's items. */
function groupDiffRuns<T>(selected: T[], replacement: T[], key: (item: T) => string): DiffRun<T>[] {
  const runs: DiffRun<T>[] = []
  let selectedIndex = 0
  let replacementIndex = 0
  let changed: { removed: T[]; added: T[] } | null = null
  for (const difference of computeTokenDiff(selected.map(key), replacement.map(key), false)) {
    const count = difference.tokens.length
    if (difference.operation === 'equal') {
      if (changed) runs.push({ kind: 'changed', ...changed })
      changed = null
      runs.push({ kind: 'same', items: replacement.slice(replacementIndex, replacementIndex + count) })
      selectedIndex += count
      replacementIndex += count
    } else if (difference.operation === 'delete') {
      changed ??= { removed: [], added: [] }
      changed.removed.push(...selected.slice(selectedIndex, selectedIndex + count))
      selectedIndex += count
    } else {
      changed ??= { removed: [], added: [] }
      changed.added.push(...replacement.slice(replacementIndex, replacementIndex + count))
      replacementIndex += count
    }
  }
  if (changed) runs.push({ kind: 'changed', ...changed })
  return runs
}

function splitTableBlocks(markdown: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = []
  for (const token of markdownParser.lexer(markdown)) {
    if (token.type === 'table') {
      blocks.push({ raw: token.raw, table: token as Tokens.Table })
    } else {
      blocks.push({ raw: token.raw })
    }
  }
  return blocks
}

function renderChangedBlocks(removed: MarkdownBlock[], added: MarkdownBlock[]): string {
  let selectedIndex = 0
  let replacementIndex = 0
  let result = ''
  while (selectedIndex < removed.length || replacementIndex < added.length) {
    let selectedText = ''
    let replacementText = ''
    while (selectedIndex < removed.length && !removed[selectedIndex].table) selectedText += removed[selectedIndex++].raw
    while (replacementIndex < added.length && !added[replacementIndex].table) replacementText += added[replacementIndex++].raw
    result += buildWordDiff(selectedText, replacementText)

    const selectedTable = removed[selectedIndex]?.table
    const replacementTable = added[replacementIndex]?.table
    if (selectedTable || replacementTable) {
      result += buildTableDiff(selectedTable, replacementTable)
      if (selectedTable) selectedIndex++
      if (replacementTable) replacementIndex++
    }
  }
  return result
}

function buildTableDiff(selected: Tokens.Table | undefined, replacement: Tokens.Table | undefined): string {
  const columnCount = Math.max(selected?.header.length ?? 0, replacement?.header.length ?? 0)
  const header = renderTableRow(selected?.header ?? [], replacement?.header ?? [], columnCount)
  const delimiter = renderTableDelimiter(replacement?.align ?? selected?.align ?? [], columnCount)
  const rowKey = (row: Tokens.TableCell[]) => JSON.stringify(row.map((cell) => cell.text))
  const rows = groupDiffRuns(selected?.rows ?? [], replacement?.rows ?? [], rowKey).flatMap((run) => {
    if (run.kind === 'same') return run.items.map((row) => renderTableRow(row, row, columnCount))
    // Pair removed and added rows in order, so an edited row shows its changes cell by cell.
    const pairs = Math.max(run.removed.length, run.added.length)
    return Array.from({ length: pairs }, (_, index) => renderTableRow(run.removed[index] ?? [], run.added[index] ?? [], columnCount))
  })
  return `\n\n${header}${delimiter}${rows.join('')}\n\n`
}

function renderTableRow(before: Tokens.TableCell[], after: Tokens.TableCell[], columnCount: number): string {
  const cells = Array.from({ length: columnCount }, (_, index) => {
    const cellDiff = computeWordDiff(before[index]?.text ?? '', after[index]?.text ?? '')
      .map((segment) => {
        if (segment.operation === 'equal') return segment.text
        const tag = segment.operation === 'delete' ? 'del' : 'ins'
        return `<${tag}>${prepareDiffText(segment.text)}</${tag}>`
      })
      .join('')
    // Marked unescapes cell pipes; escape them again so inline redlines cannot create columns.
    return cellDiff.replaceAll('|', '\\|')
  })
  return `| ${cells.join(' | ')} |\n`
}

function renderTableDelimiter(alignment: Tokens.Table['align'], columnCount: number): string {
  const cells = Array.from({ length: columnCount }, (_, index) => {
    if (alignment[index] === 'center') return ':---:'
    if (alignment[index] === 'right') return '---:'
    if (alignment[index] === 'left') return ':---'
    return '---'
  })
  return `| ${cells.join(' | ')} |\n`
}

function buildWordDiff(selectedMarkdown: string, replacementMarkdown: string): string {
  return computeWordDiff(selectedMarkdown, replacementMarkdown)
    .map((segment) => {
      if (segment.operation === 'equal') return segment.text
      // Keep a changed line break outside the tag: inside, its backslash would escape the closing tag.
      const lineBreak = segment.text.endsWith('\\') ? '\\' : ''
      const text = segment.text.slice(0, segment.text.length - lineBreak.length)
      return (text ? wrapDiffTag(segment.operation === 'delete' ? 'del' : 'ins', text) : '') + lineBreak
    })
    .join('')
}

function computeWordDiff(selectedMarkdown: string, replacementMarkdown: string): DiffSegment[] {
  const selectedTokens = tokenize(selectedMarkdown)
  const replacementTokens = tokenize(replacementMarkdown)
  return computeTokenDiff(selectedTokens, replacementTokens, true).map((difference) => ({ operation: difference.operation, text: difference.tokens.join('') }))
}

function computeTokenDiff(selectedTokens: string[], replacementTokens: string[], cleanupSemantic: boolean): TokenDiff[] {
  const { selectedChars, replacementChars, tokens } = encodeTokens(selectedTokens, replacementTokens)
  const diff = new DiffMatchPatch()
  const segments = diff.diff_main(selectedChars, replacementChars)
  if (cleanupSemantic) diff.diff_cleanupSemantic(segments)

  return segments.map(([operation, chars]) => ({
    operation: operation === -1 ? 'delete' : operation === 1 ? 'insert' : 'equal',
    tokens: Array.from(chars).map((character) => tokens[character.charCodeAt(0)]),
  }))
}

function tokenize(markdown: string): string[] {
  const tokens: string[] = []
  let previousEnd = 0

  for (const match of markdown.matchAll(kCombinedCitationRegex)) {
    const start = match.index ?? 0
    tokens.push(...tokenizeMarkdownWords(markdown.slice(previousEnd, start)))
    tokens.push(match[0])
    previousEnd = start + match[0].length
  }

  tokens.push(...tokenizeMarkdownWords(markdown.slice(previousEnd)))
  return tokens
}

function tokenizeMarkdownWords(markdown: string): string[] {
  // Keep formatting delimiters equal around changed words so diff tags remain inside
  // valid emphasis/strikethrough boundaries instead of consuming half of the syntax.
  return markdown.match(kMarkdownWordTokenRegex) ?? []
}

function encodeTokens(
  selectedTokens: string[],
  replacementTokens: string[]
): {
  selectedChars: string
  replacementChars: string
  tokens: string[]
} {
  const characterByToken = new Map<string, string>()
  const tokens: string[] = []

  const encode = (values: string[]) =>
    values
      .map((value) => {
        let character = characterByToken.get(value)
        if (character === undefined) {
          if (tokens.length >= 65_535) throw new Error('The selected passage is too large to diff.')
          character = String.fromCharCode(tokens.length)
          characterByToken.set(value, character)
          tokens.push(value)
        }
        return character
      })
      .join('')

  return {
    selectedChars: encode(selectedTokens),
    replacementChars: encode(replacementTokens),
    tokens,
  }
}

function wrapDiffTag(tag: 'del' | 'ins', text: string): string {
  return text
    .split(/(\n\n+)/)
    .map((part) => {
      if (!part || /^\n\n+$/.test(part)) return part

      const heading = part.match(/^(#{1,6})\s+(.+)$/)
      if (heading) {
        return `<h${heading[1].length}><${tag}>${heading[2]}</${tag}></h${heading[1].length}>`
      }

      const listPrefix = part.match(kListPrefixRegex)?.[1]
      if (listPrefix) {
        return `${listPrefix}<${tag}>${part.slice(listPrefix.length)}</${tag}>`
      }

      return `<${tag}>${prepareDiffText(part)}</${tag}>`
    })
    .join('')
}

function prepareDiffText(text: string): string {
  if (text.trim()) return text
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/ /g, '&nbsp;')
}
